/**
 * Real geocoded Seattle addresses (borrowed from the Home Health Demo tenant's
 * fictional patients) so seeded patients and clinicians produce sensible drive
 * times. `[street, zip, "lat,lng"]`.
 *
 * `ADDRESS_POOL` houses the *generated* census. The generator hands out one
 * address per patient and never reuses one: it filters the pool against every
 * address the fixtures already spend (the cast, `CENSUS_ADDRESSES`, the roster
 * homes and the branch) on both street+zip and coordinates, then takes the next
 * unused address in the patient's territory. So each territory's bucket has to
 * be at least as large as the number of generated patients the territory mix
 * sends to it (North 22, East 13, South 10, West 7 of 52 today) or
 * `generateSql.ts` throws rather than quietly seating two patients in one house.
 */
import { territoryForZip } from "../engine/territory";
import { DEFAULT_CONFIG } from "./config";
import { PATIENT_SLOTS } from "./patients";

export type SeedAddress = [string, string, string];

export const ADDRESS_POOL: SeedAddress[] = [
  ["10047 32Nd Ave NE", "98125", "47.70248,-122.2945029"],
  ["100 NW 84Th St", "98117", "47.6900298,-122.3581892"],
  ["1026 NE 64Th St", "98115", "47.6751401,-122.3159059"],
  ["103 NE 60Th St", "98115", "47.6720947,-122.3279861"],
  ["1048 NE 103Rd St", "98125", "47.7032792,-122.3157082"],
  ["1051 NE 106Th St", "98125", "47.7059907,-122.317003"],
  ["1052 NE 102Nd St", "98125", "47.7023565,-122.3154837"],
  ["10548 39Th Ave NE", "98125", "47.7062034,-122.286441"],
  ["10634 Durland Ave NE", "98125", "47.7064382,-122.2773086"],
  ["10701D Greenwood Ave N", "98133", "47.707059,-122.355746"],
  ["107 N 77Th St", "98103", "47.684525,-122.3575933"],
  ["11019 Palatine Ave N", "98133", "47.70938959999999,-122.3571196"],
  ["1110 N 140Th St", "98133", "47.7307134,-122.3443801"],
  ["11235 Palatine Ave N", "98133", "47.7116997,-122.3571368"],
  ["11314 35Th Ave NE", "98125", "47.7106722,-122.2902806"],
  ["11343 15Th Ave NE", "98125", "47.7116169,-122.3128539"],
  ["11500 35Th Ave NE", "98125", "47.712193,-122.2903095"],
  ["11504 Bartlett Ave NE", "98125", "47.7123508,-122.2828801"],
  ["11719 33Rd Pl NE", "98125", "47.7141094,-122.2918803"],
  ["117 N 92Nd St", "98103", "47.6958408,-122.3572042"],
  ["11920 Lakeside Ave NE", "98125", "47.7158273,-122.2802448"],
  ["12001 33Rd Ave NE", "98125", "47.7156886,-122.293179"],
  ["12026 2Nd Ave NE", "98125", "47.716699,-122.3272191"],
  ["12047 20Th Ave NE", "98125", "47.7172805,-122.3075683"],
  ["12216 Densmore Ave N", "98133", "47.71843579999999,-122.3366661"],
  ["12301 33Rd Ave NE", "98125", "47.7176152,-122.2931454"],
  ["12611 10Th Ave NE", "98125", "47.7206157,-122.3184692"],
  ["13010 3Rd Ave NE", "98125", "47.7234752,-122.3254099"],
  ["13057 28Th Ave NE", "98125", "47.7245365,-122.2981082"],
  ["13222 35Th Ave NE", "98125", "47.7254319,-122.290626"],
  ["1348 NE 62Nd St", "98115", "47.6740849,-122.3124379"],
  ["13505 23Rd Ave NE", "98125", "47.7267847,-122.3042893"],
  ["13530 Riviera Pl NE", "98125", "47.7272824,-122.2810066"],
  ["13533 41St Ave NE", "98125", "47.7271191,-122.2842717"],
  ["13555 Roosevelt Way N", "98133", "47.7277108,-122.3329904"],
  ["13708 37Th Ave NE", "98125", "47.72861349999999,-122.2879499"],
  ["1005 S Snoqualmie St", "98108", "47.5615125,-122.3200928"],
  ["1019 26Th Ave E", "98112", "47.6279605,-122.2989346"],
  ["1026 34Th Ave E", "98112", "47.628243,-122.287787"],
  ["107 Belmont Ave E", "98102", "47.6187963,-122.3242851"],
  ["1107 E Howell St", "98122", "47.617438,-122.3179815"],
  ["110 E Roanoke St", "98102", "47.6433652,-122.32661"],
  ["111 32Nd Ave E", "98112", "47.6192466,-122.2915334"],
  ["1128A 10Th Ave E", "98102", "47.629526,-122.3199831"],
  ["117A 17Th Ave E", "98112", "47.6190361,-122.3103877"],
  ["1214 42Nd Ave E", "98112", "47.6302927,-122.2789253"],
  ["1227 20Th Ave E", "98112", "47.6314185,-122.3063217"],
  ["1322 Yakima Ave S", "98144", "47.5915798,-122.2942252"],
  ["135 30Th Ave E", "98112", "47.6198916,-122.2937906"],
  ["1407C 19Th Ave", "98122", "47.6132753,-122.307976"],
  ["1424 Belmont Ave", "98122", "47.6137008,-122.3241579"],
  ["1427 Martin Luther King Jr Way", "98122", "47.6138051,-122.2965893"],
  ["1504 31St Ave", "98122", "47.6143682,-122.2921241"],
  ["1512 17Th Ave S", "98144", "47.5895755,-122.3102573"],
  ["1517 Lake Washington Blvd S", "98144", "47.5883072,-122.2896873"],
  ["1547 30Th Ave S", "98144", "47.5881133,-122.2939397"],
  ["1610 20Th Ave E", "98112", "47.633539,-122.305772"],
  ["1622 E Thomas St", "98112", "47.620653,-122.3104172"],
  ["1704 36Th Ave S", "98144", "47.5874536,-122.2866404"],
  ["1714 27Th Ave", "98122", "47.61692,-122.2972598"],
  ["1723 Madrona Dr", "98122", "47.617189,-122.2849328"],
  ["1728 34Th Ave", "98122", "47.6173644,-122.2889095"],
  ["1744 28Th Ave S", "98144", "47.5870008,-122.296693"],
  ["1768 Valentine Pl S", "98144", "47.58714550000001,-122.307112"],
  ["1801 19Th Ave", "98122", "47.617733,-122.3076521"],
  ["1825 30Th Ave", "98122", "47.6184392,-122.2939038"],
  ["10024 36Th Ave SW", "98146", "47.513026,-122.3772732"],
  ["1009 SW Elmgrove St", "98106", "47.5299568,-122.3485824"],
  ["10443 34Th Ave SW", "98146", "47.5087302,-122.3750204"],
  ["11434 Marine View Dr SW", "98146", "47.5000794,-122.3781441"],
  ["4708 25th Ave SW", "98106", "47.5607416,-122.3638824"],
  ["4617 SW Stevens St", "98116", "47.5772791,-122.3915706"],
  ["3516 SW Kenyon St", "98126", "47.5321345,-122.3774755"],
  ["4006 SW Concord St", "98136", "47.52376049999999,-122.383514"],
  ["4012 SW 107th St", "98146", "47.5076196,-122.383507"],
  ["5220 25th Ave SW", "98106", "47.554309,-122.3639982"],
  ["5016 SW Alaska St", "98116", "47.5613552,-122.3972751"],
  ["3610 SW Kenyon St", "98126", "47.53225030000001,-122.3785436"],
  ["4104 SW Raymond St", "98136", "47.5487519,-122.3847615"],
  ["5466 21st Ave SW", "98106", "47.55183419999999,-122.3596994"],
  ["3707 SW Thistle St", "98126", "47.5281372,-122.3797546"],
  ["4210 SW Holly St", "98136", "47.5431682,-122.3863013"],
  ["6708 14th Ave SW", "98106", "47.5425183,-122.3523847"],
  ["3743 SW Southern St", "98126", "47.5296775,-122.381167"],
  ["4408 SW Holly St", "98136", "47.5432643,-122.3892294"],
  ["7751 Highland Park Way SW", "98106", "47.5331865,-122.3472313"],
  ["1000 4Th Ave", "98104", "47.6067659,-122.3326439"],
  ["1000 Fairview Ave N", "98109", "47.6279899,-122.331314"],
  ["161 Lynn St", "98109", "47.6389582,-122.3538489"],
  ["1620 1St Ave W", "98119", "47.63424759999999,-122.358019"],
  ["2001 8Th Ave", "98121", "47.6158512,-122.3370442"],
  ["2003 Western Ave", "98121", "47.6108061,-122.344936"],
  ["2131 Western Ave", "98121", "47.6115119,-122.3456557"],
  ["2224 1St Ave W", "98119", "47.6392023,-122.3579438"],
  ["2403 1St Ave N", "98109", "47.6397898,-122.3558543"],
  ["2504 30Th Ave W", "98199", "47.6414205,-122.3952814"],
  ["2505 W Armour St", "98199", "47.6444451,-122.3892831"],
  ["2815A 4Th Ave W", "98119", "47.6454054,-122.3624442"],
  ["2821 Queen Anne Ave N", "98109", "47.6455574,-122.3571309"],
  ["2903 Mayfair Ave N", "98109", "47.6457694,-122.3526711"],
  ["610 Pine St", "98101", "47.6128922,-122.335313"],
  ["510 Maynard Ave S", "98104", "47.5979696,-122.324788"],
  ["615 8th Ave", "98104", "47.6055575,-122.3266344"],
  ["2910 2nd Ave N", "98109", "47.645987,-122.3531195"],
  ["3011 Warren Ave N", "98109", "47.6472677,-122.3547305"],
  ["2918 9th Ave W", "98119", "47.6465046,-122.3690831"],
  ["3010 W Government Way", "98199", "47.6615624,-122.3967578"],
  ["3212 33rd Ave W", "98199", "47.6491762,-122.399222"],
];

/**
 * Home addresses for the roster (handle → address).
 *
 * **A clinician lives in a territory they own.** The seed's drive heuristic
 * already assumes it — `supplyTerritoriesFor` returns a clinician's *owned*
 * territories and `generateSql.ts` prices both the drive in and the drive home
 * from `territories[0]` — but the *product* prices a day for real from these
 * coordinates whenever a visit is moved or **Optimize** is accepted, and it read
 * the address rather than the territory. Five homes contradicted the ownership
 * they were seeded against (Dana in Burien owning North, Priya in Lake City
 * owning South, Luis in Broadview owning West, Sam in Magnolia owning South,
 * Tasha in Burien with North as her primary), so a repriced day opened and
 * closed with a cross-town commute the branch would never staff. Each of the five
 * now lives in `territories[0]`. The three per diems own nothing, so their home
 * zip *is* their supply territory (Devon → West, Jordan → East) and
 * moving one would move where their supply lands; leave them alone. Ana, Rachel
 * and Elena carry all four territories, so any service-area home is defensible.
 *
 * Every address here is real and geocoded, borrowed from the other demo tenants
 * the way `ADDRESS_POOL` always has been, and deliberately *not* in that pool —
 * `FIXTURE_ADDRESSES` subtracts these from the census's buckets, so a home taken
 * out of the pool is a house the generated census can no longer use.
 */
export const ROSTER_ADDRESSES: Record<string, SeedAddress> = {
  // North's owner, in North: Maple Leaf / Lake City, the middle of the 98125 cluster she works.
  dana: ["2045 NE 104th St", "98125", "47.7037079,-122.3050109"],
  // East first, West second: Madrona, a few blocks from the branch.
  marcus: ["3006 E Union St", "98122", "47.6130947,-122.2934001"],
  // South's owner, in South: High Point, the middle of West Seattle.
  priya: ["6602 High Point Dr SW", "98126", "47.5433963,-122.3671803"],
  // Per diem: 98199 keeps his derived supply territory West.
  devon: ["3601 26th Pl W", "98199", "47.6524485,-122.3891013"],
  // West's owner, who covers North: lower Queen Anne, with Aurora north to the cover work.
  luis: ["400 Aloha St", "98109", "47.6273469,-122.348645"],
  // East first, South second: North Beacon Hill.
  kim: ["2114 S State St", "98144", "47.5880093,-122.3044594"],
  // All four territories; South is as defensible as any, and it is where she was.
  ana: ["8131 7th Ave SW", "98106", "47.5290973,-122.3444744"],
  // South first, East second: North Admiral, at the West Seattle end of the bridge.
  sam: ["4021 38th Ave SW", "98126", "47.5676596,-122.3804307"],
  // All four territories; North, where she was.
  rachel: ["1610 NE 77th St", "98115", "47.6850452,-122.3104141"],
  // Per diem: 98108 keeps his derived supply territory East.
  jordan: ["4010 25th Ave S", "98108", "47.5678956,-122.2999091"],
  // All four territories; West, where she was.
  elena: ["610 W Comstock St", "98119", "47.6305132,-122.3649189"],
  // North primary, South second: Wallingford, the south edge of North.
  tasha: ["1054 N 39th St", "98103", "47.6540917,-122.3442899"],
  // Per diem: 98103 keeps his derived supply territory North.
};

export const BRANCH_ADDRESS: SeedAddress = ["1107 E Howell St", "98122", "47.603118,-122.289612"];

/**
 * Normalized keys for one household: street + zip, and the geocode to five
 * decimals. Two patients matching on *either* read on a clinician's day as a
 * visit with no drive between them, so both are unique across the tenant.
 */
export function addressKeys(street: string, zip: string, coordinates: string): [string, string] {
  const geo = coordinates
    .split(",")
    .map((n) => Number(n).toFixed(5))
    .join(",");
  return [`${street.toLowerCase().replace(/\s+/g, " ").trim()}|${zip}`, geo];
}

/** Every address the fixtures already spend: the cast, the staged census (a retired slot's too), the roster's homes and the branch. */
export const FIXTURE_ADDRESSES: SeedAddress[] = [
  ...PATIENT_SLOTS.map((p): SeedAddress => [p.address1, p.zip, p.coordinates]),
  ...Object.values(ROSTER_ADDRESSES),
  BRANCH_ADDRESS,
];

/**
 * What the generated census may draw from, by territory: pool entries no fixture
 * already uses, deduplicated inside the pool as well. `generateSql.ts` hands each
 * one out once and throws when a territory runs dry, so a bucket has to be at
 * least as large as the number of generated patients its territory takes.
 */
export const UNUSED_ADDRESSES_BY_TERRITORY: Record<string, SeedAddress[]> = (() => {
  const seen = new Set(FIXTURE_ADDRESSES.flatMap((a) => addressKeys(a[0], a[1], a[2])));
  const out: Record<string, SeedAddress[]> = {};
  for (const address of ADDRESS_POOL) {
    const keys = addressKeys(address[0], address[1], address[2]);
    if (keys.some((k) => seen.has(k))) continue;
    keys.forEach((k) => seen.add(k));
    const territory = territoryForZip(DEFAULT_CONFIG.branch, address[1]) ?? "East";
    (out[territory] ??= []).push(address);
  }
  return out;
})();
